import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Search, Wand2, Check, Upload, Download, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SKUMappingDialog
 * -----------------
 * Lets a user resolve unmatched marketplace SKUs against internal SKU Master.
 *
 * Props:
 *   open, onOpenChange
 *   unmatched   – array of { external_id, asin, model_number, title, suggestions:[] }
 *   country     – "IN" etc.
 *   poNumber    – optional; when supplied, "Apply & Re-match PO" is enabled
 *   onApplied   – callback after user saves mappings (so parent can refresh)
 */
export default function SKUMappingDialog({ open, onOpenChange, unmatched = [], country = 'IN', poNumber = null, onApplied }) {
  const [skus, setSkus] = useState([]);
  const [rows, setRows] = useState([]);        // {external_id, sku_code, suggestions, search}
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Preload SKUs for the country
    (async () => {
      try {
        const res = await axios.get(`${API}/skus?country=${country}`, { headers: getAuthHeaders() });
        const list = Array.isArray(res.data) ? res.data : (res.data.skus || []);
        setSkus(list);
      } catch (e) {
        toast.error('Failed to load SKU Master');
      }
    })();
    // Seed rows from unmatched, defaulting sku_code to best suggestion (if any)
    setRows(unmatched.map(u => ({
      external_id: u.external_id || u.asin || '',
      asin: u.asin || '',
      model_number: u.model_number || '',
      title: u.title || '',
      suggestions: u.suggestions || [],
      sku_code: '',                // require explicit confirmation
      search: '',
    })));
  }, [open, unmatched, country]);

  const skuOptions = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    const filtered = q
      ? skus.filter(s =>
          (s.sku_code || '').toLowerCase().includes(q) ||
          (s.product_name || '').toLowerCase().includes(q) ||
          (s.brand || '').toLowerCase().includes(q))
      : skus;
    return filtered.slice(0, 100);
  }, [skus, globalSearch]);

  const filteredForRow = (search) => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return [];
    return skus.filter(s =>
      (s.sku_code || '').toLowerCase().includes(q) ||
      (s.product_name || '').toLowerCase().includes(q)
    ).slice(0, 6);
  };

  const setRowValue = (idx, patch) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const mappedCount = rows.filter(r => r.sku_code).length;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/sku-aliases/upload-file?country=${country}`,
        form,
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || 'Aliases uploaded');
      if (res.data.invalid?.length) {
        toast.warning(`${res.data.invalid.length} row(s) skipped`, { description: res.data.invalid.slice(0, 3).map((i) => `${i.alias} (${i.reason})`).join(', ') });
      }
      // Re-apply to PO if we have context, else notify parent
      if (poNumber) {
        await applyToPO();
      } else if (onApplied) {
        onApplied();
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await axios.get(`${API}/sku-aliases/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sku_alias_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleSaveAll = async () => {
    const aliases = rows
      .filter(r => r.sku_code && r.external_id)
      .map(r => ({ alias: r.external_id, sku_code: r.sku_code, source: 'manual' }));
    if (aliases.length === 0) {
      toast.warning('Pick at least one SKU to map before saving');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/sku-aliases/bulk`,
        { aliases, country },
        { headers: getAuthHeaders() });
      toast.success(res.data.message || `${aliases.length} mappings saved`);
      if (poNumber) {
        await applyToPO();
      } else if (onApplied) {
        onApplied();
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const applyToPO = async () => {
    if (!poNumber) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/sku-aliases/apply-to-po`,
        { po_number: poNumber, country },
        { headers: getAuthHeaders() });
      toast.success(res.data.message);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-match PO');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sku-mapping-dialog">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-black tracking-tight flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-[#007AFF]" />
            Map Marketplace SKUs → Inventory SKUs
          </DialogTitle>
          <DialogDescription className="sr-only">
            Resolve unmatched marketplace vendor codes against your internal SKU master. Use the suggestions or the search box to pick the correct SKU, then save to persist aliases.
          </DialogDescription>
          <div className="flex items-center justify-between mt-1">
            <p className="text-[12px] text-gray-500">
              {rows.length} unmatched code(s) · <span className="font-semibold text-[#34C759]">{mappedCount} ready to save</span>
            </p>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="sku-alias-file-input"
              />
              <Button
                variant="ghost"
                onClick={handleTemplateDownload}
                className="h-8 text-[11px] px-2 text-gray-500 hover:text-gray-800"
                data-testid="sku-alias-template-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> Template
              </Button>
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="h-8 text-[11px] px-3 border-[#007AFF]/30 text-[#007AFF] hover:bg-blue-50"
                data-testid="sku-alias-upload-btn"
              >
                <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload CSV/Excel'}
              </Button>
            </div>
          </div>
          <div className="mt-2 flex items-start gap-2 rounded-md bg-purple-50 border border-purple-200/50 px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-purple-600 mt-0.5 shrink-0" />
            <p className="text-[11px] text-purple-800">
              <span className="font-bold">Auto-Learn is ON</span> — vendor codes that fuzzy-match at ≥95% with clear margin are saved as aliases automatically during PO imports (source: <span className="font-semibold">auto_learned</span>). Lower confidence still needs your review here.
            </p>
          </div>
        </DialogHeader>

        {/* Global search hint */}
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Global SKU search (helps you locate codes / brand names)…"
            value={globalSearch}
            onChange={e => setGlobalSearch(e.target.value)}
            className="pl-9 h-9 text-[12px]"
            data-testid="sku-map-global-search"
          />
        </div>

        <div className="overflow-y-auto flex-1 border border-black/5 rounded-lg">
          <table className="w-full text-[12px]" data-testid="sku-mapping-table">
            <thead className="bg-gray-50/70 sticky top-0 z-10">
              <tr className="text-gray-500">
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em]">Vendor Code</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em]">ASIN / Title</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em]">Suggestions</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em]">Map to Inventory SKU</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="4" className="text-center py-8 text-gray-500">No unmatched SKUs</td></tr>
              )}
              {rows.map((r, i) => {
                const searchResults = filteredForRow(r.search);
                return (
                  <tr key={r.external_id || r.asin || r.model_number} className="border-t border-black/5" data-testid={`sku-map-row-${i}`}>
                    <td className="px-3 py-2 sp-num font-semibold text-gray-800 align-top">
                      {r.external_id}
                      {r.model_number && (
                        <div className="text-[10px] text-gray-500 font-normal mt-0.5">Model: {r.model_number}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-[11px] text-gray-500 sp-num">{r.asin}</div>
                      <div className="text-[11px] text-gray-600 max-w-[240px] truncate" title={r.title}>{r.title}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {r.suggestions && r.suggestions.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {r.suggestions.slice(0, 3).map((s, si) => (
                            <button
                              key={s.sku_code}
                              onClick={() => setRowValue(i, { sku_code: s.sku_code, search: '' })}
                              className={`text-left rounded-md px-2 py-1 border transition text-[11px] ${r.sku_code === s.sku_code ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-gray-200 hover:border-gray-400 bg-white'}`}
                              data-testid={`sku-map-suggestion-${i}-${si}`}
                            >
                              <span className="sp-num font-semibold">{s.sku_code}</span>
                              <span className="ml-2 text-[10px] font-bold text-[#34C759]">{s.score}%</span>
                              {s.product_name && <div className="text-[10px] text-gray-500 truncate max-w-[220px]">{s.product_name}</div>}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-500 italic">no auto-suggestions</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top w-[260px]">
                      <div className="relative">
                        <Input
                          value={r.sku_code || r.search}
                          placeholder="type to search…"
                          onChange={e => setRowValue(i, { search: e.target.value, sku_code: '' })}
                          className="h-8 text-[12px] sp-num"
                          data-testid={`sku-map-input-${i}`}
                        />
                        {r.sku_code && (
                          <Check className="h-3.5 w-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-[#34C759]" />
                        )}
                        {!r.sku_code && searchResults.length > 0 && (
                          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-black/10 rounded-md shadow-lg max-h-56 overflow-y-auto">
                            {searchResults.map((s, si) => (
                              <div
                                key={s.sku_code}
                                onClick={() => setRowValue(i, { sku_code: s.sku_code, search: '' })}
                                className="px-2 py-1.5 cursor-pointer hover:bg-gray-50 border-b border-black/5 last:border-b-0"
                                data-testid={`sku-map-option-${i}-${si}`}
                              >
                                <div className="sp-num text-[11px] font-semibold">{s.sku_code}</div>
                                <div className="text-[10px] text-gray-500 truncate">{s.product_name}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="pt-3 border-t border-black/5">
          <div className="flex items-center justify-between w-full">
            <div className="text-[11px] text-gray-500">
              Global search results: <span className="sp-num font-semibold">{skuOptions.length}</span> · Total SKUs: <span className="sp-num font-semibold">{skus.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="sku-map-cancel-btn" className="h-9 text-[12px]">Cancel</Button>
              <Button
                onClick={handleSaveAll}
                disabled={saving || applying || mappedCount === 0}
                className="h-9 bg-[#007AFF] hover:bg-[#005bb5] text-white text-[12px] font-semibold px-4"
                data-testid="sku-map-save-btn"
              >
                {saving || applying ? 'Saving…' : `Save ${mappedCount} mapping${mappedCount !== 1 ? 's' : ''}${poNumber ? ' & re-match PO' : ''}`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
